import React, { useMemo } from "react";
import { useFormik, FormikProvider } from "formik";
import { Scrollbars } from "react-custom-scrollbars-2";
import { v4 as uuid } from "uuid";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useConstant } from "@hooks/useConstant";
import {
    TransactionRuleCondition,
    TransactionRule,
    TransactionRuleAction,
    RuleGroupType,
} from "@common/types/transactionRule";
import { SplitConfigSchema } from "@common/schemas/rule.schema";
import {
    GL_ACCOUNT_REQUIRED_FOR_SPLIT_ERROR,
    hasSplitMissingAccount,
    PERSONAL_CATEGORY_BUSINESS_ENTITY_ERROR,
} from "@common/helpers/rules";
import { NewTransactionRuleDefinition } from "@components/transactionRules/TransactionRuleCreation/TransactionRuleCreationDrawer";
import { useEntities } from "@hooks/useEntities";
import { useCategoriesById } from "@hooks/useCategoriesById";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { submitHelper } from "@helpers/form";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { NormalizedDescriptionInfo } from "@common/types/normalizedDescription";
import { useWorkspaceRuleGroupsList } from "@api/rules.api";
import { ExcludeSplitTransactionsCheckbox } from "../TransferRuleForm/TransferRuleConditionsForm/ExcludeSplitTransactions";
import { hasSplitCategoryEntityConflict } from "./splitCategoryEntityValidation";
import { RuleConditionsForm } from "./RuleConditionsForm/RuleConditionsForm";
import { RuleActionsForm } from "./RuleActionsForm/RuleActionsForm";
import { RuleNotesForm } from "./RuleNotesForm";
import {
    TransactionRuleFormSchema,
    FormRuleConditionType,
    FormRuleActionType,
    createDefaultSplitAction,
} from "./constants";
import {
    conditionModelToFormRow,
    getDefaultFormRow,
    modelToFormActions,
} from "./modelToForm.helpers";
import {
    conditionFormRowToModel,
    formActionsToModel,
    mapSplitConfigToSingleAmount,
} from "./formToModel.helpers";
import { hasCompleteExplicitConditions } from "./explicitConditions";
import { normalizeNote } from "./note.helpers";
import styles from "./RuleForm.module.scss";
import { ApplyRuleToExistingTransactions } from "./ApplyRuleToExistingTransactions";
import { RuleTransactionsPreview } from "./RuleTransactionsPreview/RuleTransactionsPreview";
import { RuleGroupForm } from "./RuleGroupForm/RuleGroupForm";

export interface TransactionRuleSubmitData {
    rule: {
        conditions: Array<
            Omit<TransactionRuleCondition, "id"> & { id?: string }
        >;
        actions: Array<Omit<TransactionRuleAction, "id"> & { id?: string }>;
        groupId: string;
        note?: string | null;
    };
    applyToExistingTransactions: boolean;
    excludedTransactionsIds: number[];
    includedTransactionsIds: number[];
    excludeMatchedTransfers?: boolean;
}

interface Props {
    rule?: TransactionRule;
    onSubmit: (data: TransactionRuleSubmitData) => Promise<void>;
    isSubmitting: boolean;
    submitText: string;
    newRuleDefinition?: NewTransactionRuleDefinition;
    groupType?: RuleGroupType;
    onTypeChange?: (type: RuleGroupType) => void;
    normalizedDescriptions?: NormalizedDescriptionInfo[];
}

export const RuleForm: React.FC<Props> = (props) => {
    const ruleGroupsList = useWorkspaceRuleGroupsList();

    if (!ruleGroupsList.data) {
        return null;
    }

    return <RuleFormInner {...props} />;
};

const RuleFormInner: React.FC<Props> = ({
    rule,
    onSubmit,
    isSubmitting,
    submitText,
    newRuleDefinition,
    groupType,
    onTypeChange,
    normalizedDescriptions,
}) => {
    const entities = useEntities({
        excludeMockEntities: true,
    });
    const businessEntities = useMemo(
        () => entities.filter((entity) => entity.isBusiness),
        [entities],
    );
    const entitiesById = useMemo(
        () => new Map(entities.map((entity) => [entity.id, entity])),
        [entities],
    );
    const categoriesById = useCategoriesById();
    const ruleGroupsList = useWorkspaceRuleGroupsList();
    const { enabled: isGlFirst } = useIsGlFirst();
    const { error } = useToaster();

    const initialData = rule ?? newRuleDefinition;
    const shouldRequireExplicitConditions =
        !rule && newRuleDefinition?.requireExplicitConditions === true;
    const initialRowIdBase = useConstant(() => Date.now());

    const initialValues = useMemo(() => {
        const conditions = initialData?.conditions
            ?.map((c) =>
                conditionModelToFormRow({
                    conditionModel: c,
                    categoriesById,
                }),
            )
            .filter((v) => v !== undefined);

        let initialGroupId = initialData?.groupId;

        const initialConditions = conditions?.length
            ? conditions
            : [
                  {
                      id: initialRowIdBase.toString(),
                      ...getDefaultFormRow(),
                      conditionType: FormRuleConditionType.ENTITY,
                      entityValue:
                          shouldRequireExplicitConditions &&
                          newRuleDefinition?.scopeEntityId !== undefined
                              ? [newRuleDefinition.scopeEntityId]
                              : businessEntities.map((e) => e.id),
                  },
                  {
                      id: (initialRowIdBase + 1).toString(),
                      ...getDefaultFormRow(),
                  },
              ];

        const initialGroup = ruleGroupsList.data?.find((group) => {
            if (initialGroupId) {
                return group.id === initialGroupId;
            }

            return groupType !== undefined
                ? group.type === groupType && group.isDefault
                : group.isDefault;
        });

        const intialActions = initialData?.actions
            ? modelToFormActions(initialData.actions)
            : [
                  initialGroup?.type === RuleGroupType.SPLIT
                      ? createDefaultSplitAction()
                      : {
                            id: uuid(),
                            transferDirection: null,
                        },
              ];

        const firstInitialActionType = intialActions.at(0)?.actionType;
        if (
            !initialGroupId &&
            groupType !== RuleGroupType.TRANSACTION && // honor an explicit caller group type
            firstInitialActionType &&
            [
                FormRuleActionType.ACCOUNT_ID,
                FormRuleActionType.ACCOUNT_NAME,
                FormRuleActionType.ACCRUAL_ACCOUNT_ID,
            ].includes(firstInitialActionType)
        ) {
            initialGroupId ??= ruleGroupsList.data?.find(
                (group) =>
                    group.isDefault && group.type === RuleGroupType.ACCOUNTING,
            )?.id;
        }

        initialGroupId ??= initialGroup?.id;

        const initialNote =
            initialData && "note" in initialData
                ? (initialData.note ?? "")
                : "";

        return {
            groupId: initialGroupId ?? "",
            conditions: initialConditions,
            actions: intialActions,
            note: initialNote,
            applyToExistingTransactions:
                newRuleDefinition?.applyToExistingTransactions ?? true,
            excludedTransactionsIds: [],
            includedTransactionsIds: [],
            initialGroup,
        };
    }, [
        initialData,
        shouldRequireExplicitConditions,
        groupType,
        newRuleDefinition,
        ruleGroupsList.data,
        businessEntities,
        categoriesById,
        initialRowIdBase,
    ]);

    const formik = useFormik<TransactionRuleFormSchema>({
        initialValues,
        validate: ({ actions, conditions }) => {
            if (
                shouldRequireExplicitConditions &&
                !hasCompleteExplicitConditions(conditions)
            ) {
                const message =
                    "Choose and complete at least one matching condition";
                error(message);
                return { conditions: message };
            }

            const output = formActionsToModel(actions);

            if (!output.length) {
                const message = "Add at least one valid action";
                error(message);
                return { actions: message };
            }
        },
        validateOnChange: false,
        validateOnBlur: !shouldRequireExplicitConditions,
        onSubmit: submitHelper({
            handler: async ({
                groupId,
                actions,
                conditions,
                note,
                applyToExistingTransactions,
                excludedTransactionsIds,
                includedTransactionsIds,
            }) => {
                const splitAction = actions.find(
                    (action) => action.actionType === FormRuleActionType.SPLIT,
                );
                if (splitAction?.value?.splitAction) {
                    const mappedConfig = mapSplitConfigToSingleAmount(
                        splitAction?.value?.splitAction,
                    );
                    const {
                        success: splitActionConfigCorrect,
                        error: splitActionError,
                    } = SplitConfigSchema.safeParse(mappedConfig);

                    if (!splitActionConfigCorrect) {
                        error(
                            splitActionError.issues
                                .map((i) => i.message)
                                .join(". "),
                        );
                        return;
                    }

                    if (
                        isGlFirst &&
                        hasSplitMissingAccount(mappedConfig.splits ?? [])
                    ) {
                        error(GL_ACCOUNT_REQUIRED_FOR_SPLIT_ERROR);
                        return;
                    }

                    const hasPersonalCategoryWithBusinessEntity =
                        hasSplitCategoryEntityConflict({
                            splits: splitAction.value.splitAction.splits,
                            categoriesById,
                            entitiesById,
                        });

                    if (hasPersonalCategoryWithBusinessEntity) {
                        error(PERSONAL_CATEGORY_BUSINESS_ENTITY_ERROR);
                        return;
                    }
                }

                await onSubmit({
                    rule: {
                        actions: formActionsToModel(actions),
                        conditions: conditions
                            .map(conditionFormRowToModel)
                            .filter((v) => v !== undefined),
                        groupId,
                        ...(!rule ? { note: normalizeNote(note) } : {}),
                    },
                    applyToExistingTransactions,
                    excludedTransactionsIds,
                    includedTransactionsIds,
                });
            },
            loading: isSubmitting,
        }),
    });

    const currentGroupType = useMemo(() => {
        const selectedGroup = ruleGroupsList.data?.find(
            (g) => g.id === formik.values.groupId,
        );
        return (
            selectedGroup?.type ??
            initialValues.initialGroup?.type ??
            RuleGroupType.SPLIT
        );
    }, [
        formik.values.groupId,
        ruleGroupsList.data,
        initialValues.initialGroup,
    ]);

    return (
        <FormikProvider value={formik}>
            <form className={styles.form} onSubmit={formik.handleSubmit}>
                <div className={styles.container}>
                    <Scrollbars>
                        <div className={styles.formContainer}>
                            <RuleGroupForm
                                groupType={currentGroupType}
                                onTypeChange={onTypeChange}
                            />
                            <RuleConditionsForm
                                groupType={currentGroupType}
                                shouldFocusEmptyCondition={
                                    shouldRequireExplicitConditions
                                }
                                normalizedDescriptions={normalizedDescriptions}
                            />
                            <RuleActionsForm
                                entities={entities}
                                entitiesById={entitiesById}
                                categoriesById={categoriesById}
                            />
                            <RuleNotesForm rule={rule} />
                        </div>
                    </Scrollbars>
                    <div className={styles.transactionsContainer}>
                        <RuleTransactionsPreview
                            rule={rule}
                            type={currentGroupType}
                        />
                    </div>
                </div>

                <footer className={styles.footer}>
                    {currentGroupType === RuleGroupType.SPLIT && (
                        <ExcludeSplitTransactionsCheckbox />
                    )}
                    <ApplyRuleToExistingTransactions label="Do not apply Action(s) to historical transactions" />
                    <ButtonWithLoader
                        variant="primary"
                        size="md"
                        type="submit"
                        loading={isSubmitting}
                        hotKey="mod+enter"
                        hotKeyOptions={{
                            enabled: !formik.isSubmitting,
                            description: "Save rule",
                        }}
                        hotKeyDisplayPosition="inline"
                    >
                        {submitText}
                    </ButtonWithLoader>
                </footer>
            </form>
        </FormikProvider>
    );
};
